/** Public weather and internal follow-up records. No Mission/device authority. */
export const REGIONAL_STATUSES = [
  'NEW',
  'VERIFYING',
  'HANDLING',
  'REVIEW_PENDING',
  'CLOSED',
] as const;
export type RegionalStatus = (typeof REGIONAL_STATUSES)[number];
export const REGIONAL_ACTIONS = [
  'START_VERIFICATION',
  'CONFIRM',
  'NOT_CONFIRMED',
  'SUBMIT_HANDLING',
  'REVIEW_PASS',
  'REVIEW_RETURN',
  'REOPEN',
] as const;
export type RegionalAction = (typeof REGIONAL_ACTIONS)[number];
export type RegionalFilter = RegionalStatus | 'OPEN' | 'ALL';
export const statusLabels: Record<RegionalStatus, string> = {
  NEW: '待核查',
  VERIFYING: '核查中',
  HANDLING: '处理中',
  REVIEW_PENDING: '待复核',
  CLOSED: '已完成',
};
export const actionLabels: Record<RegionalAction, string> = {
  START_VERIFICATION: '开始核查',
  CONFIRM: '确认需处理',
  NOT_CONFIRMED: '未证实，提交复核',
  SUBMIT_HANDLING: '提交处理结果',
  REVIEW_PASS: '复核通过并完成',
  REVIEW_RETURN: '退回继续核查／处理',
  REOPEN: '重新发起核查',
};
export interface RegionalHour {
  time: string;
  temperatureC: number | null;
  humidityPct: number | null;
  windMs: number | null;
  gustMs: number | null;
  rainMm: number | null;
  rain6hMm: number | null;
}
export interface RegionalCell {
  id: string;
  latitude: number;
  longitude: number;
  requestedPoints: { id: string; latitude: number; longitude: number }[];
  status: 'EVALUATED' | 'PARTIAL' | 'UNAVAILABLE';
  issues: string[];
  hitCount: number;
  hourly: RegionalHour[];
}
export interface RegionalOverview {
  schemaVersion: 'regional-risk-v1';
  now: string;
  region: {
    id: string;
    name: string;
    crs: 'EPSG:4326';
    bounds: { west: number; south: number; east: number; north: number };
  };
  source: {
    provider: string;
    model: string;
    attribution: string;
    state: 'READY' | 'DEGRADED' | 'STALE' | 'UNAVAILABLE';
    fetchedAt: string | null;
    modelIssuedAt: null;
    lastAttemptAt: string | null;
    nextRefreshAt: string | null;
    errorCode: string | null;
  };
  snapshot: { id: string; sha256: string; asOf: string; horizonEnd: string } | null;
  coverage: {
    requestedPoints: number;
    uniqueGrids: number;
    availableGrids: number;
    partialGrids: number;
    unavailableGrids: number;
    expectedHours: number;
  };
  rules: {
    id: 'heat' | 'wind' | 'rain' | 'dryWind';
    label: string;
    thresholdText: string;
    version: string;
  }[];
  cells: RegionalCell[];
  caseCounts: Record<RegionalStatus, number>;
  overdueCases: number;
}
export interface RegionalCase {
  id: string;
  revision: number;
  regionId: string;
  gridId: string;
  ruleId: string | null;
  origin: 'THRESHOLD' | 'MANUAL_REVIEW';
  title: string;
  description: string;
  status: RegionalStatus;
  disposition: 'CONFIRMED' | 'NOT_CONFIRMED' | null;
  snapshotId: string;
  latitude: number;
  longitude: number;
  openedAt: string;
  updatedAt: string;
  dueAt: string | null;
  assignee: string | null;
  validFrom: string | null;
  validTo: string | null;
  isOverdue: boolean;
  evidence: { snapshotId: string; rawSha256: string; ruleVersion: string | null; summary: string };
  allowedActions: RegionalAction[];
}
export interface RegionalEvent {
  id: string;
  revision: number;
  action: string;
  fromStatus: string | null;
  toStatus: string;
  note: string;
  evidenceNote: string | null;
  actorId: string;
  actorLabel: string;
  occurredAt: string;
  requestId: string;
}
export interface RegionalDetail {
  item: RegionalCase;
  events: RegionalEvent[];
}
export interface RegionalCasePage {
  items: RegionalCase[];
  total: number;
}
export interface RegionalWriteResult extends RegionalDetail {
  idempotentReplay: boolean;
}
export interface ManualCaseCommand {
  requestId: string;
  snapshotId: string;
  gridId: string;
  title: string;
  note: string;
  assignee?: string;
  dueAt?: string;
}
export interface CaseActionCommand {
  requestId: string;
  expectedRevision: number;
  action: RegionalAction;
  note: string;
  evidenceNote?: string;
  assignee?: string;
  dueAt?: string;
}
export interface RegionalSession {
  token: string;
  csrfToken: string;
  actorId: string;
  actorLabel: string;
  expiresAt: string;
}
export interface RegionalPort {
  overview(signal?: AbortSignal): Promise<RegionalOverview>;
  cases(filter: RegionalFilter, signal?: AbortSignal): Promise<RegionalCasePage>;
  detail(id: string, signal?: AbortSignal): Promise<RegionalDetail>;
  refresh(
    requestId: string,
  ): Promise<{ result: 'REFRESHED' | 'CACHED' | 'BUSY'; snapshotId: string | null }>;
  create(command: ManualCaseCommand): Promise<RegionalWriteResult>;
  act(id: string, command: CaseActionCommand): Promise<RegionalWriteResult>;
  endSession(): Promise<void>;
}

const object = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('REGIONAL_WIRE');
  return value as Record<string, unknown>;
};
const string = (value: unknown): string => {
  if (typeof value !== 'string') throw new Error('REGIONAL_WIRE');
  return value;
};
const number = (value: unknown): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) throw new Error('REGIONAL_WIRE');
  return value;
};
const count = (value: unknown): number => {
  const result = number(value);
  if (!Number.isSafeInteger(result) || result < 0) throw new Error('REGIONAL_WIRE');
  return result;
};
const boolean = (value: unknown): boolean => {
  if (typeof value !== 'boolean') throw new Error('REGIONAL_WIRE');
  return value;
};
const nullable = <T>(value: unknown, parser: (input: unknown) => T): T | null =>
  value === null ? null : parser(value);
const list = <T>(value: unknown, parser: (input: unknown) => T, maximum = 200): T[] => {
  if (!Array.isArray(value) || value.length > maximum) throw new Error('REGIONAL_WIRE');
  return value.map(parser);
};
const oneOf = <T extends string>(value: unknown, choices: readonly T[]): T => {
  if (typeof value !== 'string' || !choices.includes(value as T)) throw new Error('REGIONAL_WIRE');
  return value as T;
};
const instant = (value: unknown): string => {
  const result = string(value);
  if (!/^\d{4}-\d{2}-\d{2}T.+Z$/.test(result) || !Number.isFinite(Date.parse(result)))
    throw new Error('REGIONAL_WIRE');
  return result;
};
const coordinate = (value: unknown, limit: number): number => {
  const result = number(value);
  if (Math.abs(result) > limit) throw new Error('REGIONAL_WIRE');
  return result;
};
const hash = (value: unknown): string => {
  const result = string(value);
  if (!/^[a-f0-9]{64}$/i.test(result)) throw new Error('REGIONAL_WIRE');
  return result;
};

export function parseRegionalOverview(value: unknown): RegionalOverview {
  const doc = object(value),
    region = object(doc.region),
    bounds = object(region.bounds),
    source = object(doc.source),
    coverage = object(doc.coverage),
    counts = object(doc.caseCounts);
  const snapshot = nullable(doc.snapshot, (v) => {
    const s = object(v);
    return {
      id: string(s.id),
      sha256: hash(s.sha256),
      asOf: instant(s.asOf),
      horizonEnd: instant(s.horizonEnd),
    };
  });
  if (source.modelIssuedAt !== null) throw new Error('REGIONAL_WIRE');
  const cells = list(
    doc.cells,
    (v) => {
      const c = object(v);
      const hourly = list(
        c.hourly,
        (input) => {
          const h = object(input);
          return {
            time: instant(h.time),
            temperatureC: nullable(h.temperatureC, number),
            humidityPct: nullable(h.humidityPct, number),
            windMs: nullable(h.windMs, number),
            gustMs: nullable(h.gustMs, number),
            rainMm: nullable(h.rainMm, number),
            rain6hMm: nullable(h.rain6hMm, number),
          };
        },
        168,
      );
      if (hourly.some((h, i) => i > 0 && Date.parse(h.time) <= Date.parse(hourly[i - 1]!.time)))
        throw new Error('REGIONAL_WIRE');
      return {
        id: string(c.id),
        latitude: coordinate(c.latitude, 90),
        longitude: coordinate(c.longitude, 180),
        requestedPoints: list(
          c.requestedPoints,
          (input) => {
            const p = object(input);
            return {
              id: string(p.id),
              latitude: coordinate(p.latitude, 90),
              longitude: coordinate(p.longitude, 180),
            };
          },
          100,
        ),
        status: oneOf(c.status, ['EVALUATED', 'PARTIAL', 'UNAVAILABLE']),
        issues: list(c.issues, string),
        hitCount: count(c.hitCount),
        hourly,
      };
    },
    100,
  );
  if (new Set(cells.map((c) => c.id)).size !== cells.length) throw new Error('REGIONAL_WIRE');
  return {
    schemaVersion: oneOf(doc.schemaVersion, ['regional-risk-v1']),
    now: instant(doc.now),
    region: {
      id: string(region.id),
      name: string(region.name),
      crs: oneOf(region.crs, ['EPSG:4326']),
      bounds: {
        west: coordinate(bounds.west, 180),
        south: coordinate(bounds.south, 90),
        east: coordinate(bounds.east, 180),
        north: coordinate(bounds.north, 90),
      },
    },
    source: {
      provider: string(source.provider),
      model: string(source.model),
      attribution: string(source.attribution),
      state: oneOf(source.state, ['READY', 'DEGRADED', 'STALE', 'UNAVAILABLE']),
      fetchedAt: nullable(source.fetchedAt, instant),
      modelIssuedAt: null,
      lastAttemptAt: nullable(source.lastAttemptAt, instant),
      nextRefreshAt: nullable(source.nextRefreshAt, instant),
      errorCode: nullable(source.errorCode, string),
    },
    snapshot,
    coverage: {
      requestedPoints: count(coverage.requestedPoints),
      uniqueGrids: count(coverage.uniqueGrids),
      availableGrids: count(coverage.availableGrids),
      partialGrids: count(coverage.partialGrids),
      unavailableGrids: count(coverage.unavailableGrids),
      expectedHours: count(coverage.expectedHours),
    },
    rules: list(
      doc.rules,
      (v) => {
        const r = object(v);
        return {
          id: oneOf(r.id, ['heat', 'wind', 'rain', 'dryWind']),
          label: string(r.label),
          thresholdText: string(r.thresholdText),
          version: string(r.version),
        };
      },
      20,
    ),
    cells,
    caseCounts: {
      NEW: count(counts.NEW),
      VERIFYING: count(counts.VERIFYING),
      HANDLING: count(counts.HANDLING),
      REVIEW_PENDING: count(counts.REVIEW_PENDING),
      CLOSED: count(counts.CLOSED),
    },
    overdueCases: count(doc.overdueCases),
  };
}
export function parseRegionalCase(value: unknown): RegionalCase {
  const c = object(value),
    e = object(c.evidence);
  return {
    id: string(c.id),
    revision: count(c.revision),
    regionId: string(c.regionId),
    gridId: string(c.gridId),
    ruleId: nullable(c.ruleId, string),
    origin: oneOf(c.origin, ['THRESHOLD', 'MANUAL_REVIEW']),
    title: string(c.title),
    description: string(c.description),
    status: oneOf(c.status, REGIONAL_STATUSES),
    disposition: nullable(c.disposition, (v) => oneOf(v, ['CONFIRMED', 'NOT_CONFIRMED'])),
    snapshotId: string(c.snapshotId),
    latitude: coordinate(c.latitude, 90),
    longitude: coordinate(c.longitude, 180),
    openedAt: instant(c.openedAt),
    updatedAt: instant(c.updatedAt),
    dueAt: nullable(c.dueAt, instant),
    assignee: nullable(c.assignee, string),
    validFrom: nullable(c.validFrom, instant),
    validTo: nullable(c.validTo, instant),
    isOverdue: boolean(c.isOverdue),
    evidence: {
      snapshotId: string(e.snapshotId),
      rawSha256: hash(e.rawSha256),
      ruleVersion: nullable(e.ruleVersion, string),
      summary: string(e.summary),
    },
    allowedActions: list(c.allowedActions, (v) => oneOf(v, REGIONAL_ACTIONS), 7),
  };
}
export function parseRegionalCases(value: unknown): RegionalCasePage {
  const page = object(value);
  return { items: list(page.items, parseRegionalCase), total: count(page.total) };
}
export function parseRegionalDetail(value: unknown): RegionalDetail {
  const doc = object(value);
  return {
    item: parseRegionalCase(doc.item),
    events: list(
      doc.events,
      (v) => {
        const e = object(v);
        return {
          id: string(e.id),
          revision: count(e.revision),
          action: string(e.action),
          fromStatus: nullable(e.fromStatus, string),
          toStatus: string(e.toStatus),
          note: string(e.note),
          evidenceNote: nullable(e.evidenceNote, string),
          actorId: string(e.actorId),
          actorLabel: string(e.actorLabel),
          occurredAt: instant(e.occurredAt),
          requestId: string(e.requestId),
        };
      },
      2000,
    ),
  };
}
export function parseRegionalWrite(value: unknown): RegionalWriteResult {
  return {
    ...parseRegionalDetail(value),
    idempotentReplay: boolean(object(value).idempotentReplay),
  };
}
export function parseRegionalSession(value: unknown): RegionalSession {
  const s = object(value);
  return {
    token: string(s.token),
    csrfToken: string(s.csrfToken),
    actorId: string(s.actorId),
    actorLabel: string(s.actorLabel),
    expiresAt: instant(s.expiresAt),
  };
}
export function parseRegionalRefresh(value: unknown) {
  const s = object(value);
  return {
    result: oneOf(s.result, ['REFRESHED', 'CACHED', 'BUSY']),
    snapshotId: nullable(s.snapshotId, string),
  };
}

export function shouldUseRegionalMonitoring(search: string): boolean {
  const q = new URLSearchParams(search);
  return (
    q.get('dataSource') !== 'demo' &&
    q.get('view') !== 'historical' &&
    q.get('view') !== 'records' &&
    q.get('mode') !== 'fire-signals'
  );
}
export function formatRegionalTime(value: string | null): string {
  if (!value || !Number.isFinite(Date.parse(value))) return '暂无时间';
  return new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(value));
}
export function displayRegionalValue(value: number | null | undefined, unit: string): string {
  return typeof value === 'number' && Number.isFinite(value)
    ? `${value.toFixed(1)} ${unit}`
    : '暂无数据';
}
export function actionNeedsEvidence(action: RegionalAction): boolean {
  return action === 'SUBMIT_HANDLING' || action === 'REVIEW_PASS';
}
export function validateRegionalAction(
  action: RegionalAction,
  note: string,
  evidence: string,
): string | null {
  if (action !== 'START_VERIFICATION' && note.trim().length < 5)
    return '请填写至少 5 个字的操作说明。';
  if (note.trim().length > 4000 || evidence.trim().length > 4000) return '说明不能超过 4000 个字。';
  if (actionNeedsEvidence(action) && evidence.trim().length < 5)
    return '处理或复核结果需要至少 5 个字的证据说明。';
  if (evidence.trim().length > 0 && evidence.trim().length < 5)
    return '证据说明可留空；填写时至少需要 5 个字。';
  return null;
}
export function localDateTimeToUtc(value: string): string | undefined {
  if (!value) return undefined;
  // The product displays China time regardless of the browser's OS timezone.
  const time = Date.parse(`${value}:00+08:00`);
  return Number.isFinite(time) ? new Date(time).toISOString() : undefined;
}
